/* ==========================================================================
   Triple 3 Music — Shopify storefront runtime
   Shopify Ajax Cart, Predictive Search, accessible overlays, and slideshow.
   ========================================================================== */

(() => {
  'use strict';

  const WISHLIST_KEY = 'triple3_wishlist';
  const focusableSelector = [
    'a[href]',
    'summary',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let activeOverlay = null;
  let overlayTrigger = null;
  let searchController = null;
  let searchTimer = null;
  let themeConfigCache = null;
  let cartMutationPending = false;
  let lastCartError = null;
  /** @type {Map<HTMLButtonElement, boolean>} */
  const cartControlStates = new Map();

  function getThemeConfig() {
    if (themeConfigCache) return themeConfigCache;
    const configElement = document.getElementById('Triple3ThemeConfig');
    try {
      themeConfigCache = JSON.parse(configElement?.textContent || '{}');
    } catch (error) {
      console.error('Unable to parse theme configuration:', error);
      themeConfigCache = {};
    }
    return themeConfigCache;
  }

  function themeString(key, fallback) {
    const value = getThemeConfig().strings?.[key];
    return typeof value === 'string' && value ? value : fallback;
  }

  function storefrontRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function localizedPath(path) {
    return `${storefrontRoot()}${path}`.replace(/\/{2,}/g, '/');
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      },
      ...options
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message =
        payload?.description ||
        payload?.message ||
        getThemeConfig().strings?.cartError ||
        themeString('cartError', 'Shopify could not complete this request.');
      throw new Error(message);
    }

    if (payload === null) {
      throw new Error(
        themeString('cartInvalidResponse', 'Shopify returned an invalid response.')
      );
    }

    return payload;
  }

  function formatMoney(cents, currencyCode) {
    const currency = currencyCode || getThemeConfig().currency || 'IDR';
    const locale = getThemeConfig().locale || document.documentElement.lang || 'id-ID';

    const config = getThemeConfig();
    const centsValue = Number(cents);
    const amount = Number.isFinite(centsValue) ? centsValue / 100 : 0;
    const shopFormat = currency === (config.shopCurrency || config.currency) ? config.moneyFormat : null;
    const placeholder = typeof shopFormat === 'string' ? shopFormat.match(/{{\s*(\w+)\s*}}/) : null;
    /** @type {Record<string, { precision: number, thousands: string, decimal: string }>} */
    const formats = {
      amount: { precision: 2, thousands: ',', decimal: '.' },
      amount_no_decimals: { precision: 0, thousands: ',', decimal: '.' },
      amount_with_comma_separator: { precision: 2, thousands: '.', decimal: ',' },
      amount_no_decimals_with_comma_separator: { precision: 0, thousands: '.', decimal: ',' }
    };
    const format = placeholder && formats[placeholder[1]];
    if (format) {
      const [integer, fraction] = amount.toFixed(format.precision).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
      const value = grouped + (fraction ? format.decimal + fraction : '');
      const output = shopFormat.replace(placeholder[0], value);
      return new DOMParser().parseFromString(output, 'text/html').body.textContent.trim();
    }

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'IDR' ? 0 : 2,
        maximumFractionDigits: currency === 'IDR' ? 0 : 2
      }).format(Number(cents || 0) / 100);
    } catch {
      return `${currency} ${amount.toLocaleString()}`;
    }
  }

  function formatPredictivePrice(value) {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value.trim())) {
      return value.trim();
    }

    const majorUnits = Number(value);
    if (!Number.isFinite(majorUnits)) return '';
    return formatMoney(Math.round(majorUnits * 100));
  }

  function createElement(tagName, className, text) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function getLiveRegion() {
    let region = document.getElementById('StorefrontStatus');
    if (!region) {
      region = createElement('div', 'visually-hidden');
      region.id = 'StorefrontStatus';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }
    return region;
  }

  function announce(message) {
    const region = getLiveRegion();
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  function showToast(message, type = 'status') {
    let toast = document.querySelector('.toast');
    if (!toast) {
      toast = createElement('div', 'toast');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.dataset.type = type;
    toast.classList.add('toast--visible');
    window.clearTimeout(toast._triple3Timeout);
    toast._triple3Timeout = window.setTimeout(() => {
      toast.classList.remove('toast--visible');
    }, 3200);
    announce(message);
  }

  function setCartControlsBusy(isBusy) {
    document
      .querySelectorAll(
        '.card-product__quick-add button, [data-product-submit], [data-product-buy-now], [data-sticky-add], [data-sticky-buy], .product__cta-btn, .cart-drawer__qty button, .cart-drawer__remove'
      )
      .forEach((control) => {
        if (isBusy) {
          if (!cartControlStates.has(control)) cartControlStates.set(control, control.disabled);
          control.disabled = true;
        } else if (cartControlStates.has(control)) {
          control.disabled = cartControlStates.get(control);
        }
        control.setAttribute('aria-busy', String(isBusy));
      });
    if (!isBusy) cartControlStates.clear();
    document.dispatchEvent(new CustomEvent('cart:busy', { detail: { busy: isBusy } }));
  }

  function updateCartCount(cart) {
    const count = Number(cart?.item_count || 0);
    document
      .querySelectorAll(
        '.cart-count-bubble, .mobile-bottom-nav__bubble, .header__drawer-meta-badge'
      )
      .forEach((bubble) => {
        bubble.textContent = String(count);
        bubble.dataset.count = String(count);
        bubble.hidden = count === 0;
      });
  }

  function validateCart(cart) {
    if (!cart || !Array.isArray(cart.items) ||
        !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 ||
        !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 ||
        !cart.items.every((item) => typeof item.key === 'string' && item.key && Number.isSafeInteger(item.quantity) && item.quantity > 0) ||
        cart.items.reduce((total, item) => total + item.quantity, 0) !== cart.item_count) {
      throw new Error(themeString('cartInvalidResponse', 'Shopify returned an invalid response.'));
    }
    return cart;
  }

  async function getCart() {
    const cart = validateCart(await requestJSON(localizedPath('cart.js')));
    updateCartCount(cart);
    return cart;
  }

  function dispatchCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart }
      })
    );
  }

  async function addToCart(
    variantId,
    quantity = 1,
    properties = {},
    sellingPlanId = null
  ) {
    if (cartMutationPending) return null;
    const id = Number(variantId);
    const qty = Number(quantity);
    if (!Number.isFinite(id) || id <= 0) {
      showToast(
        themeString('cartSelectOption', 'Please select an available product option.'),
        'error'
      );
      return null;
    }
    if (!Number.isSafeInteger(qty) || qty < 1) {
      showToast(themeString('cartInvalidQuantity', 'Please enter a valid quantity.'), 'error');
      return null;
    }

    cartMutationPending = true;
    lastCartError = null;
    setCartControlsBusy(true);
    try {
      const cartItem = { id, quantity: qty, properties };
      if (sellingPlanId) cartItem.selling_plan = Number(sellingPlanId);

      await requestJSON(localizedPath('cart/add.js'), {
        method: 'POST',
        body: JSON.stringify({
          items: [cartItem]
        })
      });
      let cart;
      try {
        cart = await getCart();
      } catch {
        lastCartError = themeString('cartAddedRefreshFailed', 'Item added to cart, but the cart could not refresh. Open your cart before adding again.');
        showToast(lastCartError, 'error');
        return null;
      }
      dispatchCartUpdated(cart);
      showToast(themeString('cartAdded', 'Item added to cart.'));
      await renderCartDrawer(cart);
      openCartDrawer({ refresh: false });
      return cart;
    } catch (error) {
      lastCartError = error.message;
      showToast(lastCartError, 'error');
      return null;
    } finally {
      cartMutationPending = false;
      setCartControlsBusy(false);
    }
  }

  async function updateCartItem(lineKey, quantity) {
    if (cartMutationPending) return null;
    const qty = Number(quantity);
    if (!Number.isSafeInteger(qty) || qty < 0 || !lineKey) {
      showToast(themeString('cartInvalidQuantity', 'Please enter a valid quantity.'), 'error');
      return null;
    }
    cartMutationPending = true;
    lastCartError = null;
    setCartControlsBusy(true);
    try {
      const cart = await requestJSON(localizedPath('cart/change.js'), {
        method: 'POST',
        body: JSON.stringify({
          id: String(lineKey),
          quantity: qty
        })
      });
      validateCart(cart);
      updateCartCount(cart);
      dispatchCartUpdated(cart);
      await renderCartDrawer(cart);
      announce(
        qty === 0
          ? themeString('cartRemoved', 'Item removed from cart.')
          : themeString('cartUpdated', 'Cart quantity updated.')
      );
      return cart;
    } catch (error) {
      lastCartError = error.message;
      showToast(lastCartError, 'error');
      return null;
    } finally {
      cartMutationPending = false;
      setCartControlsBusy(false);
    }
  }

  async function updateCartNote(note) {
    try {
      const cart = await requestJSON(localizedPath('cart/update.js'), {
        method: 'POST',
        body: JSON.stringify({ note: String(note || '') })
      });
      validateCart(cart);
      updateCartCount(cart);
      dispatchCartUpdated(cart);
      announce(themeString('cartNoteSaved', 'Order note saved.'));
      return cart;
    } catch (error) {
      showToast(error.message, 'error');
      return null;
    }
  }

  function appendLineProperties(container, item) {
    const details = [];

    if (item.variant_title && item.variant_title !== 'Default Title') {
      details.push(item.variant_title);
    }

    if (item.selling_plan_allocation?.selling_plan?.name) {
      details.push(item.selling_plan_allocation.selling_plan.name);
    }

    Object.entries(item.properties || {}).forEach(([key, value]) => {
      if (key.startsWith('_') || value === '' || value === null) return;
      details.push(`${key}: ${value}`);
    });

    if (details.length > 0) {
      container.appendChild(
        createElement('p', 'cart-drawer__item-options', details.join(' · '))
      );
    }
  }

  function createCartLine(item, currency) {
    const line = createElement('div', 'cart-drawer__item');
    line.dataset.lineKey = item.key;

    const media = createElement('a', 'cart-drawer__item-media');
    media.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title || '';
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      media.appendChild(image);
    }

    const info = createElement('div', 'cart-drawer__item-info');
    const title = createElement(
      'a',
      'cart-drawer__item-title',
      item.product_title
    );
    title.href = item.url;
    info.appendChild(title);
    appendLineProperties(info, item);

    const meta = createElement('div', 'cart-drawer__item-meta');
    meta.appendChild(
      createElement(
        'span',
        '',
        `${item.quantity} × ${formatMoney(item.final_price, currency)}`
      )
    );
    meta.appendChild(
      createElement(
        'strong',
        '',
        formatMoney(item.final_line_price, currency)
      )
    );
    info.appendChild(meta);

    const quantity = createElement('div', 'cart-drawer__qty');
    const decrease = createElement('button', '', '−');
    decrease.type = 'button';
    decrease.setAttribute(
      'aria-label',
      themeString('cartDecrease', 'Decrease quantity for [title]').replace(
        '[title]',
        item.product_title
      )
    );
    decrease.addEventListener('click', () => {
      updateCartItem(item.key, item.quantity - 1);
    });

    const value = createElement('span', '', item.quantity);
    value.setAttribute('aria-live', 'polite');

    const increase = createElement('button', '', '+');
    increase.type = 'button';
    increase.setAttribute(
      'aria-label',
      themeString('cartIncrease', 'Increase quantity for [title]').replace(
        '[title]',
        item.product_title
      )
    );
    increase.addEventListener('click', () => {
      updateCartItem(item.key, item.quantity + 1);
    });

    const remove = createElement('button', 'cart-drawer__remove');
    remove.type = 'button';
    const removeLabel = themeString('cartRemove', 'Remove');
    remove.setAttribute('aria-label', `${removeLabel} ${item.product_title}`);
    remove.title = removeLabel;
    const trashIconUrl = getThemeConfig().icons?.trash;
    if (trashIconUrl) {
      const trashIcon = document.createElement('img');
      trashIcon.src = trashIconUrl;
      trashIcon.alt = '';
      trashIcon.width = 20;
      trashIcon.height = 20;
      remove.appendChild(trashIcon);
    } else {
      remove.textContent = removeLabel;
    }
    remove.addEventListener('click', () => {
      updateCartItem(item.key, 0);
    });

    quantity.append(decrease, value, increase);
    info.append(quantity, remove);
    line.append(media, info);
    return line;
  }

  function appendDiscounts(container, cart, currency) {
    (cart.cart_level_discount_applications || []).forEach((discount) => {
      const row = createElement('div', 'cart-drawer__discount');
      row.append(
        createElement('span', '', discount.title),
        createElement(
          'span',
          '',
          `−${formatMoney(discount.total_allocated_amount, currency)}`
        )
      );
      container.appendChild(row);
    });
  }

  async function renderCartDrawer(providedCart) {
    const body = document.getElementById('cartDrawerBody');
    const footer = document.getElementById('cartDrawerFooter');
    if (!body || !footer) return providedCart || null;

    body.setAttribute('aria-busy', 'true');
    try {
      const cart = providedCart || (await getCart());
      const currency = cart.currency || getThemeConfig().currency || 'IDR';
      body.replaceChildren();
      footer.replaceChildren();

      if (!cart.items?.length) {
        const empty = createElement('div', 'cart-drawer__empty');
        empty.append(
          createElement('p', '', themeString('cartEmpty', 'Your cart is empty.')),
          createElement(
            'p',
            'cart-drawer__empty-text',
            themeString(
              'cartEmptyDescription',
              'Browse the catalog to find instruments and studio gear.'
            )
          )
        );
        const browse = createElement(
          'a',
          'btn btn--outline btn--sm',
          themeString('cartBrowse', 'Browse Products')
        );
        browse.href = localizedPath('collections/all');
        empty.appendChild(browse);
        body.appendChild(empty);
        return cart;
      }

      cart.items.forEach((item) => {
        body.appendChild(createCartLine(item, currency));
      });

      appendDiscounts(footer, cart, currency);
      const subtotal = createElement('div', 'cart-drawer__subtotal');
      subtotal.append(
        createElement('span', '', themeString('cartSubtotal', 'Subtotal')),
        createElement('strong', '', formatMoney(cart.total_price, currency))
      );

      const shippingConfig = getThemeConfig().shipping;
      let shippingMessage = null;
      if (shippingConfig?.enabled && Number(shippingConfig.thresholdCents) > 0) {
        const threshold = Number(shippingConfig.thresholdCents);
        const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
        shippingMessage = createElement(
          'p',
          'cart-drawer__shipping',
          remaining === 0
            ? themeString(
                'cartShippingReached',
                'Free-shipping eligibility threshold reached. Final availability is confirmed at checkout.'
              )
            : themeString(
                'cartShippingRemaining',
                'Add [amount] to reach the free-shipping eligibility threshold. Final availability is confirmed at checkout.'
              ).replace('[amount]', formatMoney(remaining, currency))
        );
      }

      const viewCart = createElement(
        'a',
        'btn btn--outline btn--full',
        themeString('cartView', 'View Cart')
      );
      viewCart.href = localizedPath('cart');

      footer.append(subtotal);
      if (shippingMessage) footer.append(shippingMessage);
      footer.append(viewCart);
      return cart;
    } catch (error) {
      body.replaceChildren(
        createElement('p', 'cart-drawer__error', error.message)
      );
      showToast(error.message, 'error');
      return null;
    } finally {
      body.removeAttribute('aria-busy');
    }
  }

  function getFocusable(container) {
    return [...container.querySelectorAll(focusableSelector)].filter(
      (element) =>
        !element.hidden &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.getClientRects().length > 0
    );
  }

  function openOverlay(container, trigger) {
    if (!container) return;
    if (activeOverlay && activeOverlay !== container) {
      if (activeOverlay.id === 'cartDrawer') {
        closeCartDrawer(false);
      } else if (activeOverlay.matches('[data-mobile-drawer]')) {
        closeMobileMenu(false);
      } else {
        closeActiveOverlay(false);
      }
    }

    activeOverlay = container;
    overlayTrigger = trigger || document.activeElement;
    container.removeAttribute('inert');
    container.classList.add('is-open');
    container.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overlay-open');
    document.body.style.overflow = 'hidden';

    const firstFocusable = container.querySelector('.header__drawer-close') || getFocusable(container)[0] || container;
    window.setTimeout(() => {
      if (activeOverlay === container && !container.contains(document.activeElement)) firstFocusable.focus({ preventScroll: true });
    }, 250);
  }

  function closeActiveOverlay(restoreFocus = true) {
    if (!activeOverlay) return;
    activeOverlay.classList.remove('is-open');
    activeOverlay.setAttribute('aria-hidden', 'true');
    activeOverlay.setAttribute('inert', '');
    document.body.classList.remove('overlay-open');
    document.body.style.overflow = '';

    const trigger = overlayTrigger;
    activeOverlay = null;
    overlayTrigger = null;
    if (restoreFocus && trigger?.focus) {
      trigger.focus({ preventScroll: true });
    }
  }

  async function openCartDrawer(options = {}) {
    const drawer = document.getElementById('cartDrawer');
    const overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) return;
    if (options.refresh !== false) await renderCartDrawer();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.hidden = false;
      overlay.classList.add('is-visible');
    }
    openOverlay(drawer, options.trigger || document.activeElement);
  }

  function closeCartDrawer(restoreFocus = true) {
    const drawer = document.getElementById('cartDrawer');
    drawer?.classList.remove('is-open');
    const overlay = document.querySelector('.cart-drawer__overlay');
    overlay?.classList.remove('is-visible');
    if (overlay) overlay.hidden = true;
    if (activeOverlay?.id === 'cartDrawer') closeActiveOverlay(restoreFocus);
    drawer?.setAttribute('aria-hidden', 'true');
    drawer?.setAttribute('inert', '');
  }

  function mobileMenuElements() {
    const drawer = document.querySelector('[data-mobile-drawer]');
    return {
      drawer,
      toggle: document.querySelector('.header__menu-toggle')
    };
  }

  function toggleMobileMenu(event) {
    const { drawer, toggle } = mobileMenuElements();
    if (!drawer || !toggle) return;
    if (drawer.classList.contains('is-open')) {
      closeMobileMenu();
      return;
    }

    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute(
      'aria-label',
      themeString('menuClose', 'Close menu')
    );
    openOverlay(drawer, event?.currentTarget || toggle);

  }

  function closeMobileMenu(restoreFocus = true) {
    const { drawer, toggle } = mobileMenuElements();
    if (!drawer || !toggle) return;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', themeString('menuOpen', 'Open menu'));
    if (activeOverlay === drawer) {
      closeActiveOverlay(restoreFocus);
    } else {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
  }

  function openSearch(trigger) {
    const overlay = document.getElementById('searchOverlay');
    if (!overlay) return;
    openOverlay(overlay, trigger || document.activeElement);
    window.setTimeout(() => {
      document.getElementById('searchInput')?.focus();
    }, 50);
  }

  function closeSearch() {
    const overlay = document.getElementById('searchOverlay');
    if (activeOverlay === overlay) closeActiveOverlay();
  }

  function predictiveSearchURL(query) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor'
    });
    return `${localizedPath('search/suggest.json')}?${params.toString()}`;
  }

  function normalizePredictiveResults(payload) {
    const results = payload?.resources?.results || {};

    // Deduplicate and canonicalize collections by normalized title
    const collectionsMap = new Map();
    (results.collections || []).forEach((item) => {
      if (!item.url || item.url === '#') return;
      const cleanUrl = item.url.split('?')[0];
      const normalizedTitle = (item.title || '').trim().toLowerCase();
      if (!normalizedTitle) return;

      if (!collectionsMap.has(normalizedTitle)) {
        collectionsMap.set(normalizedTitle, { item, cleanUrl });
      } else {
        const existing = collectionsMap.get(normalizedTitle);
        if (existing.item.handle && existing.item.handle.endsWith('-1') && !item.handle?.endsWith('-1')) {
          collectionsMap.set(normalizedTitle, { item, cleanUrl });
        }
      }
    });

    const collections = Array.from(collectionsMap.values()).map(({ item, cleanUrl }) => {
      const imgUrl =
        (typeof item.image === 'string' ? item.image : item.image?.url) ||
        item.featured_image?.url ||
        (typeof item.featured_image === 'string' ? item.featured_image : '') ||
        '';

      return {
        group: 'collections',
        type: themeString('searchCollection', 'Collection'),
        title: item.title,
        url: cleanUrl,
        image: imgUrl,
        meta: themeString('searchCollection', 'Collection')
      };
    });

    const products = (results.products || [])
      .filter((item) => item.url && item.url !== '#')
      .map((item) => {
        const cleanUrl = item.url.split('?')[0];
        const rawVendor = (item.vendor || '').trim();
        const isGenericVendor =
          !rawVendor ||
          rawVendor.toLowerCase() === 'triple 3 music' ||
          rawVendor.toLowerCase() === (window.theme?.shopName || '').toLowerCase();
        const imgUrl =
          (typeof item.image === 'string' ? item.image : item.image?.url) ||
          item.featured_image?.url ||
          (typeof item.featured_image === 'string' ? item.featured_image : '') ||
          '';

        return {
          group: 'products',
          type: themeString('searchProduct', 'Product'),
          title: item.title,
          url: cleanUrl,
          image: imgUrl,
          vendor: !isGenericVendor ? rawVendor : '',
          price: item.price
        };
      });

    const pages = (results.pages || [])
      .filter((item) => item.url && item.url !== '#')
      .map((item) => ({
        group: 'pages',
        type: themeString('searchPage', 'Page'),
        title: item.title,
        url: item.url.split('?')[0],
        image: '',
        meta: themeString('searchPage', 'Page')
      }));

    const articles = (results.articles || [])
      .filter((item) => item.url && item.url !== '#')
      .map((item) => ({
        group: 'articles',
        type: themeString('searchArticle', 'Article'),
        title: item.title,
        url: item.url.split('?')[0],
        image: (typeof item.image === 'string' ? item.image : item.image?.url) || '',
        meta: themeString('searchArticle', 'Article')
      }));

    return {
      collections,
      products,
      pages,
      articles,
      totalCount: collections.length + products.length + pages.length + articles.length
    };
  }

  function createSearchResult(item, classPrefix) {
    const isHeader = classPrefix === 'header-search';
    const link = createElement('a', `${classPrefix}__result`);
    if (isHeader) link.classList.add('header__search-dropdown-item');
    link.href = item.url;
    link.setAttribute('role', 'option');

    const media = createElement('span', `${classPrefix}__result-media`);
    if (isHeader) media.classList.add('header__search-dropdown-media');
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.width = isHeader ? 44 : 56;
      image.height = isHeader ? 44 : 56;
      image.loading = 'lazy';
      media.appendChild(image);
    } else {
      media.classList.add(`${classPrefix}__result-media--placeholder`);
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', isHeader ? '20' : '24');
      svg.setAttribute('height', isHeader ? '20' : '24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.75');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      svg.setAttribute('aria-hidden', 'true');

      if (item.group === 'collections') {
        svg.innerHTML = '<rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>';
      } else {
        svg.innerHTML = '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline>';
      }
      media.appendChild(svg);
    }
    link.appendChild(media);

    const info = createElement('span', `${classPrefix}__result-info`);
    if (isHeader) info.classList.add('header__search-dropdown-info');

    const title = createElement('span', `${classPrefix}__result-title`, item.title);
    if (isHeader) title.classList.add('header__search-dropdown-title');
    info.appendChild(title);

    const meta = createElement('span', `${classPrefix}__result-meta`);
    if (item.group === 'products') {
      if (item.vendor) {
        meta.appendChild(
          createElement('span', `${classPrefix}__result-brand`, item.vendor)
        );
      }
      const priceLabel = formatPredictivePrice(item.price);
      if (priceLabel) {
        meta.appendChild(
          createElement('span', `${classPrefix}__result-price`, priceLabel)
        );
      }
    } else if (item.group !== 'collections') {
      if (item.type || item.meta) {
        meta.appendChild(
          createElement('span', `${classPrefix}__result-badge`, item.type || item.meta)
        );
      }
    }

    if (meta.childNodes.length > 0) {
      info.appendChild(meta);
    }
    link.appendChild(info);

    return link;
  }

  function renderPredictiveResults(input, container, data, query = '') {
    container.replaceChildren();
    container.setAttribute('role', 'listbox');
    container.dataset.highlightIndex = '-1';

    if (!data || data.totalCount === 0) {
      container.appendChild(
        createElement(
          'p',
          'predictive-search__empty',
          themeString('searchNoResults', 'No matching results.')
        )
      );
    } else {
      const prefix = container.classList.contains('header__search-dropdown')
        ? 'header-search'
        : 'search-overlay';
      let optionIndex = 0;

      const appendGroup = (items, groupTitle) => {
        if (!items || items.length === 0) return;

        const groupEl = createElement('div', `${prefix}__group`);
        const headerEl = createElement('div', `${prefix}__group-header`);
        const titleEl = createElement('span', `${prefix}__group-title`, groupTitle);
        const countEl = createElement('span', `${prefix}__group-count`, String(items.length));
        headerEl.appendChild(titleEl);
        headerEl.appendChild(countEl);
        groupEl.appendChild(headerEl);

        const listEl = createElement('div', `${prefix}__group-items`);
        items.forEach((item) => {
          const result = createSearchResult(item, prefix);
          result.id = `${container.id}-option-${optionIndex++}`;
          result.setAttribute('aria-selected', 'false');
          listEl.appendChild(result);
        });
        groupEl.appendChild(listEl);
        container.appendChild(groupEl);
      };

      appendGroup(data.collections, themeString('searchCollectionsGroup', 'Collections & Brands'));
      appendGroup(data.products, themeString('searchProductsGroup', 'Products'));
      const otherItems = [...(data.pages || []), ...(data.articles || [])];
      appendGroup(otherItems, themeString('searchPagesGroup', 'Pages & Info'));

      if (query) {
        const footer = createElement('a', `${prefix}__footer`);
        footer.href = `${localizedPath('search')}?q=${encodeURIComponent(query)}`;
        footer.setAttribute('role', 'option');
        footer.id = `${container.id}-option-${optionIndex++}`;
        footer.setAttribute('aria-selected', 'false');

        const footerText = createElement('span', `${prefix}__footer-text`);
        const viewAllPrefix = themeString('searchViewAll', 'View all results for');
        const strong = createElement('strong', '', `“${query}”`);
        footerText.appendChild(document.createTextNode(`${viewAllPrefix} `));
        footerText.appendChild(strong);
        footer.appendChild(footerText);

        const footerIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        footerIcon.setAttribute('viewBox', '0 0 24 24');
        footerIcon.setAttribute('width', '16');
        footerIcon.setAttribute('height', '16');
        footerIcon.setAttribute('fill', 'none');
        footerIcon.setAttribute('stroke', 'currentColor');
        footerIcon.setAttribute('stroke-width', '2');
        footerIcon.setAttribute('stroke-linecap', 'round');
        footerIcon.setAttribute('stroke-linejoin', 'round');
        footerIcon.setAttribute('aria-hidden', 'true');
        footerIcon.innerHTML = '<line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>';
        footer.appendChild(footerIcon);

        container.appendChild(footer);
      }
    }

    container.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  }

  async function runPredictiveSearch(input, container) {
    const query = input.value.trim();
    searchController?.abort();
    if (query.length < 2) {
      container.replaceChildren();
      container.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    }

    // Approved search app integration hook (Boost Commerce / Searchanise)
    if (typeof window.triggerAppPredictiveSearch === 'function') {
      const handled = window.triggerAppPredictiveSearch(input, query, container);
      if (handled) return;
    }

    const controller = new AbortController();
    searchController = controller;
    container.setAttribute('aria-busy', 'true');
    try {
      const payload = await requestJSON(predictiveSearchURL(query), {
        signal: controller.signal
      });
      if (controller.signal.aborted || input.value.trim() !== query) return;
      renderPredictiveResults(
        input,
        container,
        normalizePredictiveResults(payload),
        query
      );
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted && input.value.trim() === query) {
        container.replaceChildren(
          createElement(
            'p',
            'predictive-search__error',
            themeString(
              'searchError',
              'Search suggestions are temporarily unavailable.'
            )
          )
        );
        container.classList.add('is-open');
        input.setAttribute('aria-expanded', 'true');
      }
    } finally {
      if (searchController === controller) container.removeAttribute('aria-busy');
    }
  }

  function searchContainerForInput(input) {
    if (input.id === 'searchInput') {
      return document.getElementById('searchResults');
    }
    return input
      .closest('.header__search-container, .header__mobile-search')
      ?.querySelector('.header__search-dropdown');
  }

  function handleSearchInput(event) {
    const input = event.currentTarget;
    const container = searchContainerForInput(input);
    if (!container) return;
    searchController?.abort();
    window.clearTimeout(searchTimer);
    container.replaceChildren();
    container.classList.remove('is-open');
    container.removeAttribute('aria-busy');
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    if (input.value.trim().length < 2) return;
    searchTimer = window.setTimeout(() => {
      runPredictiveSearch(input, container);
    }, 180);
  }

  function handleSearchKeydown(event) {
    const input = event.currentTarget;
    const container = searchContainerForInput(input);
    if (event.key === 'Escape') {
      window.clearTimeout(searchTimer);
      searchController?.abort();
      container?.classList.remove('is-open');
      container?.removeAttribute('aria-busy');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    }
    if (!container?.classList.contains('is-open')) return;
    const results = [
      ...container.querySelectorAll('[role="option"]')
    ];
    let index = Number(container.dataset.highlightIndex || -1);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      index = Math.min(index + 1, results.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      index = Math.max(index - 1, 0);
    } else if (event.key === 'Enter' && index >= 0) {
      event.preventDefault();
      results[index].click();
      return;
    } else if (event.key === 'Escape') {
      window.clearTimeout(searchTimer);
      searchController?.abort();
      container.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    } else {
      return;
    }

    if (!results[index]) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    container.dataset.highlightIndex = String(index);
    input.setAttribute('aria-activedescendant', results[index].id);
    results.forEach((result, resultIndex) => {
      result.classList.toggle('is-highlighted', resultIndex === index);
      result.setAttribute(
        'aria-selected',
        String(resultIndex === index)
      );
    });
    results[index]?.scrollIntoView({ block: 'nearest' });
  }

  function bindPredictiveSearch(root = document) {
    root
      .querySelectorAll(
        '[data-predictive-search-input], #searchInput'
      )
      .forEach((input) => {
        if (input.dataset.predictiveSearchBound === 'true') return;
        input.dataset.predictiveSearchBound = 'true';
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        input.addEventListener('input', handleSearchInput);
        input.addEventListener('keydown', handleSearchKeydown);
      });
  }

  function wishlistItems() {
    try {
      const parsed = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  function isWishlisted(productId) {
    return wishlistItems().includes(String(productId));
  }

  function updateWishlistUI(productId, isSaved) {
    document
      .querySelectorAll(`[data-wishlist-id="${CSS.escape(String(productId))}"]`)
      .forEach((button) => {
        button.classList.toggle('is-active', isSaved);
        button.setAttribute('aria-pressed', String(isSaved));
      });
    const label = document.getElementById('wishlistText');
    if (label) {
      label.textContent = isSaved
        ? themeString('wishlistSaved', 'Saved on this device')
        : themeString('wishlistSave', 'Save on this device');
    }
  }

  function toggleWishlist(productId) {
    const id = String(productId);
    const items = wishlistItems();
    const isSaved = !items.includes(id);
    const next = isSaved
      ? [id, ...items].slice(0, 100)
      : items.filter((item) => item !== id);
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(next));
    } catch {
      showToast(
        themeString('wishlistError', 'Unable to save on this device. Please allow browser storage and try again.'),
        'error'
      );
      return items.includes(id);
    }
    updateWishlistUI(id, isSaved);
    showToast(
      isSaved
        ? themeString('wishlistSavedStatus', 'Saved to this device.')
        : themeString('wishlistRemovedStatus', 'Removed from this device.')
    );
    return isSaved;
  }

  function showSlide(slideshow, nextIndex) {
    const slides = [...slideshow.querySelectorAll('.slideshow__slide')];
    const dots = [...slideshow.querySelectorAll('.slideshow__dot')];
    if (slides.length === 0) return;
    const index = ((nextIndex % slides.length) + slides.length) % slides.length;
    slideshow.dataset.currentIndex = String(index);

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });
    dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function startSlideshow(slideshow) {
    window.clearInterval(slideshow._triple3Timer);
    if (
      slideshow.querySelectorAll('.slideshow__slide').length < 2 ||
      slideshow.dataset.autoplay === 'false' ||
      slideshow.dataset.editorPaused === 'true' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      slideshow.matches(':hover') ||
      slideshow.contains(document.activeElement) ||
      document.hidden
    ) {
      return;
    }
    const speed = Math.max(3000, Number(slideshow.dataset.speed) || 5000);
    slideshow._triple3Timer = window.setInterval(() => {
      const current = Number(slideshow.dataset.currentIndex || 0);
      showSlide(slideshow, current + 1);
    }, speed);
  }

  function moveSlideshow(direction, targetSlideshow) {
    const slideshow =
      targetSlideshow?.closest?.('.slideshow') ||
      targetSlideshow ||
      document.querySelector('.slideshow');
    if (!slideshow) return;
    showSlide(
      slideshow,
      Number(slideshow.dataset.currentIndex || 0) + Number(direction || 0)
    );
    startSlideshow(slideshow);
  }

  function initSlideshow(slideshow) {
    if (slideshow.dataset.slideshowBound === 'true') {
      startSlideshow(slideshow);
      return;
    }
    slideshow.dataset.slideshowBound = 'true';
    const activeIndex = [
      ...slideshow.querySelectorAll('.slideshow__slide')
    ].findIndex((slide) => slide.classList.contains('is-active'));
    showSlide(slideshow, Math.max(0, activeIndex));

    slideshow.querySelectorAll('.slideshow__dot').forEach((dot, index) => {
      dot.addEventListener('click', () => {
        showSlide(slideshow, index);
        startSlideshow(slideshow);
      });
    });
    slideshow.addEventListener('mouseenter', () => {
      window.clearInterval(slideshow._triple3Timer);
    });
    slideshow.addEventListener('mouseleave', () => {
      startSlideshow(slideshow);
    });
    slideshow.addEventListener('focusin', () => {
      window.clearInterval(slideshow._triple3Timer);
    });
    slideshow.addEventListener('focusout', () => {
      window.setTimeout(() => startSlideshow(slideshow), 0);
    });
    startSlideshow(slideshow);
  }

  function initUtilityBar(root = document) {
    const track = root.querySelector('[data-announcement-rotation]');
    if (!track) return;
    if (track.dataset.rotationBound === 'true') {
      track._triple3Start?.();
      return;
    }
    track.dataset.rotationBound = 'true';
    const slides = [...track.querySelectorAll('[data-announcement-slide]')];
    if (slides.length <= 1) return;
    let current = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    const showAnnouncement = (nextIndex) => {
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
    };
    showAnnouncement(current);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stop = () => {
      window.clearInterval(track._triple3Timer);
      track._triple3Timer = null;
    };
    const start = () => {
      stop();
      if (
        track.dataset.autoplay === 'false' ||
        reducedMotion.matches ||
        document.hidden ||
        track.matches(':hover') ||
        track.contains(document.activeElement)
      ) {
        return;
      }
      const speed = Math.max(3000, Number(track.dataset.speed) || 5000);
      track._triple3Timer = window.setInterval(() => {
        if (document.hidden) return;
        current = (current + 1) % slides.length;
        showAnnouncement(current);
      }, speed);
    };

    track.addEventListener('mouseenter', stop);
    track.addEventListener('mouseleave', start);
    track.addEventListener('focusin', stop);
    track.addEventListener('focusout', () => window.setTimeout(start, 0));
    track._triple3Start = start;
    track._triple3Stop = stop;
    start();
  }

  function initScrollReveal(root = document) {
    const targets = root.querySelectorAll(
      '.reveal-section:not(.is-revealed), .card-product:not(.is-revealed)'
    );
    if (!('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px' }
    );
    targets.forEach((target) => observer.observe(target));
  }

  function trapOverlayFocus(event) {
    if (event.key !== 'Tab' || !activeOverlay) return;
    const focusable = getFocusable(activeOverlay);
    if (focusable.length === 0) {
      event.preventDefault();
      activeOverlay.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!activeOverlay.contains(document.activeElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindGlobalEvents() {
    if (document.documentElement.dataset.triple3GlobalBound === 'true') return;
    document.documentElement.dataset.triple3GlobalBound = 'true';

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
        closeCartDrawer();
        closeSearch();
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch(document.activeElement);
      }
      trapOverlayFocus(event);
    });

    document.addEventListener('click', (event) => {
      const quickAdd = event.target.closest('[data-quick-add]');
      if (quickAdd) {
        event.preventDefault();
        event.stopPropagation();
        if (!quickAdd.disabled) {
          addToCart(quickAdd.dataset.variantId, quickAdd.dataset.quantity || 1);
        }
        return;
      }

      const mobileMenuToggle = event.target.closest('[data-mobile-menu-toggle]');
      if (mobileMenuToggle) {
        toggleMobileMenu({ currentTarget: mobileMenuToggle });
        return;
      }

      if (event.target.closest('[data-mobile-menu-close]')) {
        closeMobileMenu();
        return;
      }

      const cartOpen = event.target.closest('[data-cart-open]');
      if (cartOpen) {
        event.preventDefault();
        openCartDrawer({ trigger: cartOpen });
        return;
      }

      if (event.target.closest('[data-cart-close]')) {
        closeCartDrawer();
        return;
      }

      if (event.target.closest('[data-search-close]')) {
        closeSearch();
        return;
      }

      const slideshowControl = event.target.closest('[data-slideshow-direction]');
      if (slideshowControl) {
        moveSlideshow(
          Number(slideshowControl.dataset.slideshowDirection),
          slideshowControl
        );
        return;
      }

      if (
        event.target.closest(
          '.header__drawer-link, .header__drawer-submenu-link, .header__drawer-meta-link'
        )
      ) {
        closeMobileMenu(false);
      }
      if (
        !event.target.closest(
          '.header__search-container, .header__mobile-search'
        )
      ) {
        searchController?.abort();
        window.clearTimeout(searchTimer);
        document
          .querySelectorAll('.header__search-dropdown')
          .forEach((dropdown) => {
            dropdown.classList.remove('is-open');
            dropdown.removeAttribute('aria-busy');
          });
        document.querySelectorAll('[data-predictive-search-input]').forEach((input) => {
          input.setAttribute('aria-expanded', 'false');
          input.removeAttribute('aria-activedescendant');
        });
      }
    });

    document.addEventListener('visibilitychange', () => {
      document.querySelectorAll('.slideshow').forEach(startSlideshow);
      document
        .querySelectorAll('[data-announcement-rotation]')
        .forEach((track) => track._triple3Start?.());
    });

    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-localization-select]')) return;
      event.target.form?.requestSubmit();
    });

    window.addEventListener(
      'resize',
      () => {
        if (window.innerWidth >= 1024) closeMobileMenu(false);
      },
      { passive: true }
    );

    document.addEventListener('click', (event) => {
      if (event.target.matches('.cart-drawer__overlay')) closeCartDrawer();
    });
  }

  function init(root = document) {
    getLiveRegion();
    bindGlobalEvents();
    bindPredictiveSearch(root);
    initUtilityBar(root);
    initScrollReveal(root);
    root.querySelectorAll('.slideshow').forEach(initSlideshow);
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target
      .querySelectorAll('.slideshow')
      .forEach((slideshow) => window.clearInterval(slideshow._triple3Timer));
    event.target
      .querySelectorAll('[data-announcement-rotation]')
      .forEach((track) => track._triple3Stop?.());
  });

  window.Triple3App = {
    addToCart,
    getCartError: () => lastCartError,
    clearCart: async () => {
      const cart = await requestJSON(localizedPath('cart/clear.js'), {
        method: 'POST',
        body: JSON.stringify({})
      });
      validateCart(cart);
      updateCartCount(cart);
      dispatchCartUpdated(cart);
      await renderCartDrawer(cart);
      return cart;
    },
    closeCartDrawer,
    closeMobileMenu,
    closeSearch,
    formatPrice: formatMoney,
    getConfig: getThemeConfig,
    isCartBusy: () => cartMutationPending,
    getCart,
    getCartCount: async () => (await getCart()).item_count,
    isWishlisted,
    moveSlideshow,
    openCartDrawer,
    openSearch,
    refreshCartDrawer: renderCartDrawer,
    removeFromCart: (lineKey) => updateCartItem(lineKey, 0),
    showToast,
    showSlide,
    startSlideshow,
    toggleMobileMenu,
    toggleWishlist,
    updateCartCount: () => getCart(),
    updateCartItem,
    updateCartNote
  };
})();
